import {onMounted, onUnmounted} from 'vue';
import {FetchError} from 'ofetch';
import {useDataTracking, useLogger, useMaltVisitorId} from '#imports';
import {useAmplitudeSessionReplayLoader} from './useAmplitudeSessionReplayLoader';

/**
 * Starts Amplitude Session Replay.
 *
 * @param startIf The function is evaluated on mount and should returns true to start the recording.
 * If no function is provided, the recording starts automatically.
 */
export function useAmplitudeSessionReplay(startIf?: () => boolean | Promise<boolean>) {
    const logger = useLogger();
    const maltVisitorId = useMaltVisitorId();
    const {track, getSessionTimestamp} = useDataTracking();
    const {loadSessionReplay, sessionReplay} = useAmplitudeSessionReplayLoader();

    let intervalId: number | undefined;
    let cancelled = false;

    onMounted(async () => {
        if (startIf && !(await startIf())) {
            return;
        }

        if (!maltVisitorId.value) {
            logger.error('Unable to start session replay without maltVisitorId');
            return;
        }

        try {
            const trackingSessionReplay = await loadSessionReplay();

            if (!trackingSessionReplay) {
                return;
            }

            let sessionTimestamp = await getSessionTimestamp();

            // If abort before init
            if (cancelled) {
                return;
            }

            await trackingSessionReplay.init(sessionTimestamp.amplitudeApiKey, {
                serverZone: 'EU',
                deviceId: maltVisitorId.value || undefined,
                sessionId: sessionTimestamp.timestamp,
                logLevel: import.meta.dev ? 2 : 0,
            }).promise;

            // If abort after init
            if (cancelled) {
                trackingSessionReplay.shutdown();
                return;
            }

            track('session_replay_started');

            window.clearInterval(intervalId);

            intervalId = window.setInterval(async () => {
                try {
                    sessionTimestamp = await getSessionTimestamp();
                    trackingSessionReplay.setSessionId(sessionTimestamp.timestamp);
                } catch (e) {
                    _handleGetSessionTimestampError(e);
                }
            }, sessionTimestamp.timeout - 60000); // Update sessionId 1 minute before the timeout to ensure we don't miss any events
        } catch (e) {
            if (!cancelled) {
                _handleGetSessionTimestampError(e);
            }
        }
    });

    onUnmounted(() => {
        cancelled = true;

        const trackingSessionReplay = sessionReplay.value;

        if (trackingSessionReplay) {
            trackingSessionReplay.shutdown();
        }

        window.clearInterval(intervalId);
        intervalId = undefined;
    });

    function _handleGetSessionTimestampError(e: unknown) {
        if (e instanceof FetchError && e.statusCode === 403) {
            // Backend can decide the client is not legit and return 403 Forbidden status code. From a frontend
            // perspective, it's not an error: it means we don't want to setup session replay.
            return;
        }
        // Any other error is reported.
        logger.error('An error occured while updating session replay sessionId', e);
    }
}
